import { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import date from 'date-and-time';

import { spectatorRegisterFreeWespin } from '../../ducks/we-spin-duck';
import Checkbox from '~/components/forms/checkbox';
import BinaryQuestion from '~/components/binary-question';
import Loading from '~/components/misc/loading';
import useTranslations from '~/components/translation/use-translations';
import { authenticationSelector } from '~/components/authentication/ducks/selectors';
import { Modal } from '~/components/modal/modal-old';
import useLoginFlow from '~/hooks/use-login-flow';
import CheckboxContainer from '../binary-question-checkbox-container';
import { WE_SPIN_MODALS_TEXT_KEYS } from '../../constants/we-spin-text-keys';

/**
 * Modal for joining a free spin event.
 *
 * @param {Object} props - Component props.
 * @param {boolean} props.openJoinFreespin - Whether the join free spin modal is open.
 * @param {Function} props.closeJoinFreespin - Function to close the join free spin modal.
 * @param {string} props.streamRef - Unique reference ID for the stream.
 * @param {Date} props.startDate - Start date of the free spin event.
 * @param {Date} props.endTime - End time of the free spin event.
 * @returns {JSX.Element}
 */
const JoinFreespinModal = props => {
	const dispatch = useDispatch();
	const location = useLocation();
	const authenticated = useSelector(authenticationSelector);
	const { initializeLogin } = useLoginFlow();
	const { translations: tr, isLoadingTranslations } = useTranslations(WE_SPIN_MODALS_TEXT_KEYS);

	const formattedStartDate = useMemo(() => date.format(props.startDate, 'ddd D MMM'), [props.startDate]);
	const formattedStartTime = useMemo(() => date.format(props.startDate, 'HH:mm'), [props.startDate]);
	const formattedEndTime = useMemo(() => date.format(props.endTime, 'HH:mm'), [props.endTime]);

	const [submitting, setSubmitting] = useState(false);
	const [isReminderChecked, setIsReminderChecked] = useState(true);

	const joinFreespin = useCallback(async () => {
		if (!authenticated) {
			initializeLogin({
				from: `${location.pathname}${location.search}`
			});
			return;
		}

		try {
			setSubmitting(true);
			await dispatch(spectatorRegisterFreeWespin(Number(0), props.streamRef, () => {}, isReminderChecked));
			props.closeJoinFreespin();
		} finally {
			setSubmitting(false);
		}
	}, [authenticated, dispatch, initializeLogin, isReminderChecked, location.pathname, location.search, props]);

	if (isLoadingTranslations) return <Loading />;

	return (
		<Modal show={props.openJoinFreespin} position="fixed">
			<BinaryQuestion
				shouldOpenBinary
				openInSidebar
				buttonsHasUncontrolledLoading={submitting}
				title={tr.WE_SPIN_BUY_IN_FREE_WE_SPIN_MODAL_TITLE}
				text={tr.WE_SPIN_BUY_IN_FREE_WE_SPIN_MODAL_TEXT}
				yesText={tr.WE_SPIN_BUY_IN_CONFIRM_BUTTON}
				noText={tr.WE_SPIN_BUY_IN_CONFIRM_CANCEL_BUTTON}
				onYesClick={joinFreespin}
				onNoClick={props.closeJoinFreespin}
			>
				<div className="u-pt2">
					<table className="table--binary-question">
						<tbody>
							<tr>
								<td>{tr.WE_SPIN_BUY_IN_FREE_WE_SPIN_MODAL_DATE}</td>
								<td>{formattedStartDate}</td>
							</tr>
							<tr>
								<td>{tr.WE_SPIN_BUY_IN_FREE_WE_SPIN_MODAL_TIME}</td>
								<td>{`${formattedStartTime} - ${formattedEndTime}`}</td>
							</tr>
							<tr>
								<td>{tr.WE_SPIN_BUY_IN_REMINDER}</td>
								<CheckboxContainer>
									<Checkbox
										id="remind-me-checkbox"
										isChecked={isReminderChecked}
										onChange={() => {
											// TODO: get value from event
											setIsReminderChecked(!isReminderChecked);
										}}
										iconPosition="right"
									/>
								</CheckboxContainer>
							</tr>
						</tbody>
					</table>
				</div>
			</BinaryQuestion>
		</Modal>
	);
};

export default JoinFreespinModal;

// if (streamView.isPrivateStreamer) {
// 	navigate(transformPath(`${ROUTE_WE_SPIN_GAME_ROOM_BUY_IN_SUCCESS}/${streamView.streamRef}`));
// } else if (fromDashboard) {
// 	navigate(transformPath(ROUTE_ACCOUNT_WE_SPIN_DASHBOARD));
// } else {
// 	navigate(transformPath(`${ROUTE_WE_SPIN_GAME_ROOM_PREVIEW}/${streamView.streamRef}`));
// }
